import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEmpresaAuth } from '../../../contexts/EmpresaAuthContext';
import Layout from '../../../components/shared/Layout/Layout';
import Button from '../../../components/shared/Button/Button';
import {
  AuthContainer,
  AuthCard,
  AuthTitle,
  AuthSubtitle,
  AuthFooter,
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  ErrorMessage,
  SuccessMessage,
} from '../../../components/shared/Form/Form.styles';
import { FormSection, DocumentsSection, FileUpload, FieldGrid } from './Register.styles';

const EmpresaRegister = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    businessType: '',
    address: '',
    city: '',
    state: '',
    password: '',
    confirmPassword: '',
  });
  const [documents, setDocuments] = useState({
    businessLicense: null,
    taxId: null,
    identification: null,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useEmpresaAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files[0]) {
      setDocuments(prev => ({ ...prev, [name]: files[0] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const formDataWithFiles = new FormData();
      Object.keys(formData).forEach(key => {
        if (key !== 'confirmPassword') {
          formDataWithFiles.append(key, formData[key]);
        }
      });
      Object.keys(documents).forEach(key => {
        if (documents[key]) {
          formDataWithFiles.append(key, documents[key]);
        }
      });

      const result = await register(formDataWithFiles);

      if (result.success) {
        setSuccess('¡Registro exitoso! Tu empresa será revisada por nuestro equipo. Redirigiendo...');
        setTimeout(() => navigate('/empresa/dashboard'), 2000);
      } else {
        setError(result.error || 'Error al registrar empresa');
      }
    } catch (err) {
      setError('Error de conexión. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout headerType="empresa">
      <AuthContainer>
        <AuthCard $wide>
          <AuthTitle>Registra tu empresa</AuthTitle>
          <AuthSubtitle>
            Únete a BokerHub y llega a miles de viajeros
          </AuthSubtitle>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <form onSubmit={handleSubmit}>
            <FormSection>
              <h3>Información del negocio</h3>

              <FormGroup>
                <FormLabel>Nombre de la empresa *</FormLabel>
                <FormInput
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  placeholder="Aventuras Extremas SA de CV"
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Tipo de negocio *</FormLabel>
                <FormSelect
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecciona el tipo</option>
                  <option value="parque_acuatico">Parque Acuático</option>
                  <option value="museo">Museo</option>
                  <option value="zoologico">Zoológico</option>
                  <option value="renta_vehiculos">Renta de Vehículos (Cuatrimotos, Lanchas)</option>
                  <option value="tours">Tours y Paseos</option>
                  <option value="aventura">Actividades de Aventura</option>
                  <option value="cultural">Actividades Culturales</option>
                  <option value="otro">Otro</option>
                </FormSelect>
              </FormGroup>

              <FieldGrid>
                <FormGroup>
                  <FormLabel>Ciudad *</FormLabel>
                  <FormInput
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Cancún"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Estado *</FormLabel>
                  <FormInput
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="Quintana Roo"
                    required
                  />
                </FormGroup>
              </FieldGrid>

              <FormGroup>
                <FormLabel>Dirección completa *</FormLabel>
                <FormInput
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Av. Principal 123, Centro"
                  required
                />
              </FormGroup>
            </FormSection>

            <FormSection>
              <h3>Información de contacto</h3>

              <FormGroup>
                <FormLabel>Nombre del contacto *</FormLabel>
                <FormInput
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  placeholder="Juan Pérez"
                  required
                />
              </FormGroup>

              <FieldGrid>
                <FormGroup>
                  <FormLabel>Email empresarial *</FormLabel>
                  <FormInput
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contacto@empresa.com"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Teléfono *</FormLabel>
                  <FormInput
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="998-123-4567"
                    required
                  />
                </FormGroup>
              </FieldGrid>
            </FormSection>

            <FormSection>
              <h3>Seguridad</h3>

              <FieldGrid>
                <FormGroup>
                  <FormLabel>Contraseña *</FormLabel>
                  <FormInput
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Mínimo 6 caracteres"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Confirmar contraseña *</FormLabel>
                  <FormInput
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Repite tu contraseña"
                    required
                  />
                </FormGroup>
              </FieldGrid>
            </FormSection>

            <DocumentsSection>
              <h3>Documentos de verificación</h3>
              <p>
                Nuestro equipo los revisa para aprobar tu empresa. Podrás crear
                actividades desde el primer día y publicarlas al ser verificada.
              </p>

              <FileUpload>
                <label>
                  <span>Licencia de negocio</span>
                  <input
                    type="file"
                    name="businessLicense"
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {documents.businessLicense && (
                    <span className="file-name">✓ {documents.businessLicense.name}</span>
                  )}
                </label>
              </FileUpload>

              <FileUpload>
                <label>
                  <span>RFC / Registro fiscal</span>
                  <input
                    type="file"
                    name="taxId"
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {documents.taxId && (
                    <span className="file-name">✓ {documents.taxId.name}</span>
                  )}
                </label>
              </FileUpload>

              <FileUpload>
                <label>
                  <span>Identificación oficial</span>
                  <input
                    type="file"
                    name="identification"
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {documents.identification && (
                    <span className="file-name">✓ {documents.identification.name}</span>
                  )}
                </label>
              </FileUpload>
            </DocumentsSection>

            <Button type="submit" fullWidth disabled={loading}>
              {loading ? 'Registrando empresa...' : 'Registrar empresa'}
            </Button>
          </form>

          <AuthFooter>
            <p>¿Ya tienes cuenta? <Link to="/empresa/login">Inicia sesión aquí</Link></p>
            <hr />
            <p>¿Eres viajero? <Link to="/register">Crear cuenta personal</Link></p>
          </AuthFooter>
        </AuthCard>
      </AuthContainer>
    </Layout>
  );
};

export default EmpresaRegister;
